import type { LucideIcon } from "lucide-react";
import * as Lucide from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import type { InboxDemoIconKey, InboxDemoIcons } from "../data/types";

const lucideByName = Lucide as unknown as Record<string, LucideIcon>;

const CHANNEL_ASSETS: Record<string, string> = {
  email: "/images/inbox-demo/channel/email.svg",
  wecom: "/images/inbox-demo/channel/wecom.svg",
  wechat: "/images/inbox-demo/channel/wechat.svg",
  xiaohongshu: "/images/inbox-demo/channel/xiaohongshu.svg",
  tiktok: "/images/inbox-demo/channel/tiktok.svg",
  api: "/images/inbox-demo/channel/api.svg",
  livechat: "/images/inbox-demo/channel/livechat.svg",
  instagram: "/images/inbox-demo/channel/instagram.svg",
  facebook: "/images/inbox-demo/channel/facebook.svg",
};

function lucideIcon(name: string, size: number): ReactElement {
  const Icon = lucideByName[name];
  return (
    <Icon
      className="shrink-0"
      height={size}
      strokeWidth={1.75}
      width={size}
    />
  );
}

function assetIcon(src: string, size: number, alt = ""): ReactElement {
  return (
    <img
      alt={alt}
      className="block shrink-0 object-contain"
      height={size}
      src={src}
      width={size}
    />
  );
}

function channelIcon(key: keyof typeof CHANNEL_ASSETS, size = 16): ReactElement {
  return assetIcon(CHANNEL_ASSETS[key], size);
}

export const defaultInboxDemoIcons: InboxDemoIcons = {
  logo: assetIcon("/images/inbox-demo/3chat-logo.svg", 24, "3Chat"),
  search: lucideIcon("Search", 24),
  listSearch: lucideIcon("Search", 20),
  builder: lucideIcon("WandSparkles", 24),
  agent: lucideIcon("Bot", 24),
  inbox: lucideIcon("Inbox", 24),
  knowledge: lucideIcon("LibraryBig", 24),
  contacts: lucideIcon("UsersRound", 24),
  discovery: lucideIcon("Signpost", 24),
  dashboard: lucideIcon("MessageSquareDot", 24),
  integrations: lucideIcon("ArrowLeftRight", 24),
  settings: lucideIcon("Settings", 24),
  support: lucideIcon("Headset", 24),
  feedback: lucideIcon("MessagesSquare", 24),
  panelLeft: lucideIcon("PanelLeftClose", 24),
  percent: lucideIcon("Percent", 24),
  database: lucideIcon("Database", 24),
  package: lucideIcon("Package", 24),
  bell: lucideIcon("Bell", 24),
  email: channelIcon("email"),
  wecom: channelIcon("wecom"),
  wechat: channelIcon("wechat"),
  xiaohongshu: channelIcon("xiaohongshu"),
  tiktok: channelIcon("tiktok"),
  api: channelIcon("api"),
  livechat: channelIcon("livechat"),
  instagram: channelIcon("instagram"),
  facebook: channelIcon("facebook"),
  send: lucideIcon("Send", 16),
  emoji: lucideIcon("Smile", 20),
  attachment: lucideIcon("Paperclip", 20),
  lightning: lucideIcon("Zap", 20),
  ai: lucideIcon("Sparkles", 20),
  eyeOff: lucideIcon("EyeOff", 20),
  messageCircleOff: lucideIcon("MessageCircleOff", 20),
  eye: lucideIcon("Eye", 20),
  panelRight: lucideIcon("PanelRight", 20),
  chevronDown: lucideIcon("ChevronDown", 12),
  groupBack: lucideIcon("ArrowLeftFromLine", 16),
  circleUser: lucideIcon("CircleUserRound", 12),
};

export function resolveDemoIcon(
  icons: InboxDemoIcons,
  key?: InboxDemoIconKey,
): ReactNode {
  if (!key) return null;
  return icons[key] ?? defaultInboxDemoIcons[key] ?? null;
}
